import React, { useState, useEffect, useContext, useCallback } from 'react';
import Base from '../components/Base';
import Swal from 'sweetalert2';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';
import {
  Globe, FileText, Map, Save, Info, Copy, Check,
  ChevronRight, ExternalLink, AlertCircle, Search
} from 'lucide-react';

// ─── Constants ───────────────────────────────────────────────────────
const TABS = [
  { id: 'meta', label: 'Meta Tags', icon: Search },
  { id: 'robots', label: 'Robots.txt', icon: FileText },
  { id: 'sitemap', label: 'Sitemap', icon: Map },
];

const PAGE_KEYS = [
  { id: 'home', label: 'Home' },
  { id: 'shop', label: 'Shop' },
  { id: 'about', label: 'About' },
  { id: 'contact', label: 'Contact' },
  { id: 'product', label: 'Products' },
];

const PLACEHOLDER_META = {
  home: {
    title: 'Brand Name - Official Store | Vasstr',
    description: 'Shop exclusive handcrafted designs. Explore our latest collection of premium fashion.',
    keywords: 'designer fashion, handcrafted, premium clothing',
  },
  shop: {
    title: 'Shop All Products | Brand Name',
    description: 'Browse our complete collection. Find the perfect piece for every occasion.',
    keywords: 'shop, collection, buy online',
  },
  about: {
    title: 'About Us | Brand Name',
    description: 'Learn about our story, craftsmanship, and commitment to quality fashion.',
    keywords: 'about, story, designer',
  },
  contact: {
    title: 'Contact Us | Brand Name',
    description: 'Get in touch with us for orders, custom requests, or any inquiries.',
    keywords: 'contact, enquiry, customer support',
  },
  product: {
    title_template: '{product_name} | {brand_name}',
    description_template: 'Shop {product_name} from {brand_name}. Premium quality, exclusive design.',
  },
};

const DEFAULT_ROBOTS = `User-agent: *
Allow: /
Disallow: /cart
Disallow: /viewcart
Disallow: /checkout
Disallow: /place_order
Disallow: /login_page
Disallow: /register
Disallow: /user_profile
Disallow: /previous_order
Disallow: /forgot_password
Disallow: /password_reset`;

// ─── Helpers ─────────────────────────────────────────────────────────
const charColor = (len, max) => {
  const pct = len / max;
  if (pct > 1) return 'text-red-600';
  if (pct > 0.8) return 'text-amber-600';
  return 'text-gray-400';
};

// ─── Component ───────────────────────────────────────────────────────
const SEOSettings = () => {
  const { authToken, user } = useContext(AuthContext);

  // Tab state
  const [activeTab, setActiveTab] = useState('meta');
  const [activePage, setActivePage] = useState('home');

  // Data state
  const [pageMeta, setPageMeta] = useState({});
  const [robotsCustomRules, setRobotsCustomRules] = useState('');
  const [sitemapExcludedPaths, setSitemapExcludedPaths] = useState([]);
  const [ogImageUrl, setOgImageUrl] = useState('');

  // Sitemap preview data
  const [sitemapData, setSitemapData] = useState({ urls: [], total_count: 0, sitemap_url: '' });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${authToken?.access}` };
  const storeUrl = user ? `${user.username}.vasstr.com` : 'yourstore.vasstr.com';

  // ─── Fetch ───────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, sitemapRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/designer/seo/`, { headers }),
        axios.get(`${API_BASE_URL}/api/designer/seo/sitemap-preview/`, { headers }),
      ]);

      if (configRes.data?.success) {
        const d = configRes.data.data;
        setPageMeta(d.page_meta || {});
        setRobotsCustomRules(d.robots_custom_rules || '');
        setSitemapExcludedPaths(d.sitemap_excluded_paths || []);
        setOgImageUrl(d.og_image_url || '');
      }
      if (sitemapRes.data?.success) {
        setSitemapData(sitemapRes.data.data);
      }
    } catch (err) {
      devLog.error('Failed to load SEO config:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load SEO settings.' });
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Save ────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/api/designer/seo/`, {
        page_meta: pageMeta,
        robots_custom_rules: robotsCustomRules,
        sitemap_excluded_paths: sitemapExcludedPaths,
        og_image_url: ogImageUrl,
      }, { headers });

      if (res.data?.success) {
        Swal.fire({ icon: 'success', title: 'Saved!', text: 'SEO settings updated successfully.', timer: 2000, showConfirmButton: false });
        setDirty(false);
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res.data?.message || 'Failed to save.' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save SEO settings.';
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  // ─── Meta helpers ────────────────────────────────────────────────
  const getMetaField = (page, field) => pageMeta[page]?.[field] || '';

  const setMetaField = (page, field, value) => {
    setPageMeta(prev => ({
      ...prev,
      [page]: { ...prev[page], [field]: value },
    }));
    setDirty(true);
  };

  // ─── Sitemap helpers ─────────────────────────────────────────────
  const toggleSitemapPath = (path) => {
    setSitemapExcludedPaths(prev => {
      const next = prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path];
      return next;
    });
    setDirty(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ─── Google Preview ──────────────────────────────────────────────
  const renderGooglePreview = () => {
    const isProduct = activePage === 'product';
    let previewTitle, previewDesc;

    if (isProduct) {
      const tpl = getMetaField('product', 'title_template') || PLACEHOLDER_META.product.title_template;
      const descTpl = getMetaField('product', 'description_template') || PLACEHOLDER_META.product.description_template;
      previewTitle = tpl.replace('{product_name}', 'Silk Saree Collection').replace('{brand_name}', storeUrl.split('.')[0]);
      previewDesc = descTpl.replace('{product_name}', 'Silk Saree Collection').replace('{brand_name}', storeUrl.split('.')[0]);
    } else {
      previewTitle = getMetaField(activePage, 'title') || PLACEHOLDER_META[activePage]?.title || '';
      previewDesc = getMetaField(activePage, 'description') || PLACEHOLDER_META[activePage]?.description || '';
    }

    return (
      <div className="bg-white border border-gray-200 rounded-[9px] p-5 mt-5">
        <p className="text-xs font-medium text-gray-400 mb-3 uppercase tracking-wider">Google Search Preview</p>
        <div className="space-y-0.5 max-w-full sm:max-w-[600px]">
          <p className="text-[#1a0dab] text-[18px] leading-tight truncate" style={{ fontFamily: 'Arial, sans-serif' }}>
            {previewTitle || 'Your Page Title'}
          </p>
          <p className="text-[#006621] text-[13px]" style={{ fontFamily: 'Arial, sans-serif' }}>
            https://{storeUrl}{activePage === 'home' ? '' : `/${activePage === 'product' ? 'product/silk-saree-collection-42' : activePage}`}
          </p>
          <p className="text-[#545454] text-[13px] leading-snug line-clamp-2" style={{ fontFamily: 'Arial, sans-serif' }}>
            {previewDesc || 'Your page description will appear here...'}
          </p>
        </div>
      </div>
    );
  };

  // ─── Tab Content: Meta Tags ──────────────────────────────────────
  const renderMetaTab = () => {
    const isProduct = activePage === 'product';
    const inputClasses = 'w-full bg-white border border-gray-200 rounded-[9px] px-4 py-2.5 text-sm text-[#2C1810] placeholder-gray-300 focus:outline-none focus:border-[#C9A882] focus:ring-1 focus:ring-[#C9A882]/30 transition-colors';
    const labelClasses = 'block text-sm font-medium text-[#2C1810] mb-1.5';

    return (
      <div>
        {/* Page selector pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {PAGE_KEYS.map(p => (
            <button
              key={p.id}
              onClick={() => setActivePage(p.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activePage === p.id
                  ? 'bg-[#2C1810] text-white shadow-sm'
                  : 'bg-[#F4ECE5] text-[#2C1810] hover:bg-[#EDE3D9]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Fields */}
        <div className="space-y-5">
          {isProduct ? (
            <>
              <div>
                <label className={labelClasses}>Title Template</label>
                <input
                  type="text"
                  value={getMetaField('product', 'title_template')}
                  onChange={e => setMetaField('product', 'title_template', e.target.value)}
                  maxLength={70}
                  className={inputClasses}
                  placeholder={PLACEHOLDER_META.product.title_template}
                />
                <div className="flex justify-between mt-1.5">
                  <p className="text-xs text-gray-400">
                    Use <code className="bg-gray-100 px-1 rounded text-[11px]">{'{product_name}'}</code> and <code className="bg-gray-100 px-1 rounded text-[11px]">{'{brand_name}'}</code>
                  </p>
                  <p className={`text-xs ${charColor(getMetaField('product', 'title_template').length, 70)}`}>
                    {getMetaField('product', 'title_template').length}/70
                  </p>
                </div>
              </div>
              <div>
                <label className={labelClasses}>Description Template</label>
                <textarea
                  value={getMetaField('product', 'description_template')}
                  onChange={e => setMetaField('product', 'description_template', e.target.value)}
                  maxLength={160}
                  rows={3}
                  className={`${inputClasses} resize-none`}
                  placeholder={PLACEHOLDER_META.product.description_template}
                />
                <div className="flex justify-between mt-1.5">
                  <p className="text-xs text-gray-400">
                    Also supports <code className="bg-gray-100 px-1 rounded text-[11px]">{'{product_description}'}</code>
                  </p>
                  <p className={`text-xs ${charColor(getMetaField('product', 'description_template').length, 160)}`}>
                    {getMetaField('product', 'description_template').length}/160
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className={labelClasses}>Page Title</label>
                <input
                  type="text"
                  value={getMetaField(activePage, 'title')}
                  onChange={e => setMetaField(activePage, 'title', e.target.value)}
                  maxLength={70}
                  className={inputClasses}
                  placeholder={PLACEHOLDER_META[activePage]?.title}
                />
                <div className="flex justify-between mt-1.5">
                  <p className="text-xs text-gray-400">Shown as the blue link in Google results</p>
                  <p className={`text-xs ${charColor(getMetaField(activePage, 'title').length, 70)}`}>
                    {getMetaField(activePage, 'title').length}/70
                  </p>
                </div>
              </div>
              <div>
                <label className={labelClasses}>Meta Description</label>
                <textarea
                  value={getMetaField(activePage, 'description')}
                  onChange={e => setMetaField(activePage, 'description', e.target.value)}
                  maxLength={160}
                  rows={3}
                  className={`${inputClasses} resize-none`}
                  placeholder={PLACEHOLDER_META[activePage]?.description}
                />
                <div className="flex justify-between mt-1.5">
                  <p className="text-xs text-gray-400">Shown below the title in Google results</p>
                  <p className={`text-xs ${charColor(getMetaField(activePage, 'description').length, 160)}`}>
                    {getMetaField(activePage, 'description').length}/160
                  </p>
                </div>
              </div>
              <div>
                <label className={labelClasses}>Keywords</label>
                <input
                  type="text"
                  value={getMetaField(activePage, 'keywords')}
                  onChange={e => setMetaField(activePage, 'keywords', e.target.value)}
                  maxLength={500}
                  className={inputClasses}
                  placeholder={PLACEHOLDER_META[activePage]?.keywords}
                />
                <div className="flex justify-between mt-1.5">
                  <p className="text-xs text-gray-400">Comma-separated keywords for this page</p>
                  <p className={`text-xs ${charColor(getMetaField(activePage, 'keywords').length, 500)}`}>
                    {getMetaField(activePage, 'keywords').length}/500
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {renderGooglePreview()}
      </div>
    );
  };

  // ─── Tab Content: Robots.txt ─────────────────────────────────────
  const renderRobotsTab = () => {
    const sitemapLine = `\nSitemap: https://${storeUrl}/sitemap.xml`;
    const mergedRobots = DEFAULT_ROBOTS + (robotsCustomRules ? '\n' + robotsCustomRules : '') + sitemapLine;

    return (
      <div className="space-y-6">
        {/* Info callout */}
        <div className="flex gap-3 bg-[#F4ECE5] border border-[#C9A882]/30 rounded-[9px] p-4">
          <Info className="w-5 h-5 text-[#2C1810] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#2C1810]/80">
            Robots.txt tells search engines which pages to skip. The auto-generated rules already handle cart, checkout, and login pages.
          </p>
        </div>

        {/* Auto-generated preview */}
        <div>
          <label className="block text-sm font-medium text-[#2C1810] mb-1.5">Auto-Generated Rules</label>
          <pre className="bg-gray-50 border border-gray-200 rounded-[9px] p-4 text-xs text-gray-600 font-mono whitespace-pre-wrap overflow-x-auto select-all">
            {DEFAULT_ROBOTS}
          </pre>
        </div>

        {/* Custom rules */}
        <div>
          <label className="block text-sm font-medium text-[#2C1810] mb-1.5">Custom Rules</label>
          <textarea
            value={robotsCustomRules}
            onChange={e => { setRobotsCustomRules(e.target.value); setDirty(true); }}
            rows={5}
            className="w-full bg-white border border-gray-200 rounded-[9px] px-4 py-3 text-sm font-mono text-[#2C1810] placeholder-gray-300 focus:outline-none focus:border-[#C9A882] focus:ring-1 focus:ring-[#C9A882]/30 transition-colors resize-none"
            placeholder={`Disallow: /private-page\nAllow: /special-page`}
          />
          <p className="text-xs text-gray-400 mt-1.5">Add extra Disallow or Allow rules. One per line.</p>
        </div>

        {/* Merged preview */}
        <div>
          <label className="block text-sm font-medium text-[#2C1810] mb-1.5">Final Output Preview</label>
          <pre className="bg-[#2C1810] text-[#F4ECE5] rounded-[9px] p-4 text-xs font-mono whitespace-pre-wrap overflow-x-auto">
            {mergedRobots}
          </pre>
        </div>
      </div>
    );
  };

  // ─── Tab Content: Sitemap ────────────────────────────────────────
  const renderSitemapTab = () => {
    const included = sitemapData.urls.filter(u => !sitemapExcludedPaths.includes(u.path));
    const excluded = sitemapData.urls.filter(u => sitemapExcludedPaths.includes(u.path));

    // Group by type
    const groups = {};
    sitemapData.urls.forEach(u => {
      const type = u.page_type || 'Other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(u);
    });

    const groupOrder = ['Homepage', 'Shop', 'About', 'Contact', 'Category', 'Product'];
    const sortedGroups = groupOrder.filter(g => groups[g]).map(g => [g, groups[g]]);

    return (
      <div className="space-y-6">
        {/* Info + stats */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 flex gap-3 bg-[#F4ECE5] border border-[#C9A882]/30 rounded-[9px] p-4">
            <Info className="w-5 h-5 text-[#2C1810] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#2C1810]/80">
              Google discovers your pages via this sitemap. Uncheck pages you don't want indexed.
            </p>
          </div>
          <div className="flex gap-3">
            <div className="bg-white border border-gray-200 rounded-[9px] px-5 py-3 text-center">
              <p className="text-2xl font-semibold text-[#2C1810]">{included.length}</p>
              <p className="text-xs text-gray-400">Indexed</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-[9px] px-5 py-3 text-center">
              <p className="text-2xl font-semibold text-red-500">{excluded.length}</p>
              <p className="text-xs text-gray-400">Excluded</p>
            </div>
          </div>
        </div>

        {/* Copy sitemap URL */}
        {sitemapData.sitemap_url && (
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-[9px] px-4 py-3">
            <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <code className="text-sm text-[#2C1810] truncate flex-1">{sitemapData.sitemap_url}</code>
            <button
              onClick={() => copyToClipboard(sitemapData.sitemap_url)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F4ECE5] text-[#2C1810] text-xs font-medium rounded-full hover:bg-[#EDE3D9] transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}

        {/* URL list grouped by type */}
        <div className="space-y-4">
          {sortedGroups.map(([groupName, urls]) => (
            <div key={groupName}>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-semibold text-[#2C1810]">{groupName}</h3>
                <span className="text-xs text-gray-400">({urls.length})</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-[9px] divide-y divide-gray-100 overflow-hidden">
                {urls.map(u => {
                  const isExcluded = sitemapExcludedPaths.includes(u.path);
                  return (
                    <label
                      key={u.path}
                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${isExcluded ? 'opacity-50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={!isExcluded}
                        onChange={() => toggleSitemapPath(u.path)}
                        className="w-4 h-4 rounded border-gray-300 text-[#2C1810] focus:ring-[#C9A882]"
                      />
                      <span className={`text-sm flex-1 truncate ${isExcluded ? 'line-through text-gray-400' : 'text-[#2C1810]'}`}>
                        {u.path}
                      </span>
                      {u.product_name && (
                        <span className="text-xs text-gray-400 hidden sm:block truncate max-w-[200px]">{u.product_name}</span>
                      )}
                      <span className="text-xs text-gray-300 hidden sm:block">{u.priority}</span>
                      <span className="text-xs text-gray-300 hidden sm:block w-16 text-right">{u.changefreq}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Base Content={
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#C9A882] border-t-transparent" />
        </div>
      } />
    );
  }

  return (
    <Base Content={
      <div className="max-w-4xl mx-auto overflow-x-hidden">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#F4ECE5] flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-[#2C1810]" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-semibold text-[#2C1810] font-fell">SEO Settings</h1>
              <p className="text-xs sm:text-sm text-gray-500">Control how your store appears in Google search results</p>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 bg-[#F4ECE5] rounded-[9px] p-1 mb-6">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 flex-1 justify-center px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-[#2C1810] shadow-sm'
                    : 'text-[#2C1810]/60 hover:text-[#2C1810]'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab content card */}
        <div className="bg-white border border-gray-200 rounded-[9px] shadow-sm p-4 sm:p-6 md:p-8">
          {activeTab === 'meta' && renderMetaTab()}
          {activeTab === 'robots' && renderRobotsTab()}
          {activeTab === 'sitemap' && renderSitemapTab()}
        </div>

        {/* Save button */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
          <div>
            {dirty && (
              <span className="flex items-center gap-1.5 text-sm text-amber-600">
                <AlertCircle className="w-4 h-4" />
                Unsaved changes
              </span>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-[9px] text-sm font-medium transition-all ${
              dirty
                ? 'bg-[#2C1810] text-white hover:bg-[#1a0f0a] shadow-sm'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    } />
  );
};

export default SEOSettings;
